import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { BarChart3, MapPin, GraduationCap, Users, Download, Loader2 } from 'lucide-react';

export default function PartnerReportsPage() {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'locations', label: 'Locations', icon: MapPin },
    { id: 'courses', label: 'Courses', icon: GraduationCap },
    { id: 'demographics', label: 'Demographics', icon: Users },
  ];

  useEffect(() => {
    fetchReport(activeTab);
  }, [activeTab]);

  const fetchReport = async (type) => {
    setLoading(true);
    try {
      const res = await api.get(`/partner/reports/${type}`);
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/partner/reports/export?type=${activeTab}&format=json`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}_report.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="partner-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-rsc-navy flex items-center gap-2">
            <BarChart3 className="h-6 w-6" /> Partner Reports
          </h1>
          <p className="text-gray-500">View detailed program analytics and export data</p>
        </div>
        <Button onClick={handleExport} variant="outline" data-testid="export-report-btn">
          <Download className="h-4 w-4 mr-2" /> Export {activeTab}
        </Button>
      </div>

      <div className="flex gap-2 border-b">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`report-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-rsc-navy text-rsc-navy'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" /> {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-rsc-navy" />
        </div>
      ) : data ? (
        <Card>
          <CardHeader>
            <CardTitle className="capitalize">{activeTab} Report</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-gray-50 rounded-lg p-4 text-sm overflow-auto max-h-[500px] whitespace-pre-wrap">
              {JSON.stringify(data, null, 2)}
            </pre>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No Report Data Available</p>
            <p className="text-sm mt-1">Report data will populate as programs are running.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
